import { useEffect, useState } from 'react';
import { QuestionnaireSchedule } from '@stores/QuestionnaireAdminStore';
import { SaveQuestionnaireScheduleAction } from '@actions/Admin/Questionnaire/SaveQuestionnaireScheduleAction';
import { removeAt, replaceAt } from './questionnaireDefinitionEditing';
import {
  emptyScheduleEdit,
  emptyTimepointRow,
  ScheduleEdit,
  ScheduleTimepointRow,
  scheduleDefinitionFrom,
  scheduleEditFrom,
  scheduleProblems,
} from './scheduleDefinitionEditing';

/**
 * Runs the schedule dialog. A null schedule creates a new one. The edit starts over each time the dialog opens, and
 * a schedule a cohort uses is locked to name changes only.
 */
export const useScheduleDialogController = (
  open: boolean,
  schedule: QuestionnaireSchedule | null,
  onSaved: () => void
) => {
  const [edit, setEdit] = useState<ScheduleEdit>(emptyScheduleEdit());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEdit(schedule ? scheduleEditFrom(schedule) : emptyScheduleEdit());
    setError(null);
  }, [open, schedule]);

  const problems = scheduleProblems(edit);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const response = await new SaveQuestionnaireScheduleAction().Execute(
      schedule?.id ?? null,
      scheduleDefinitionFrom(edit)
    );
    setSaving(false);
    if (response.status !== 'SUCCESS') {
      setError(response.statusMessage);
      return;
    }
    onSaved();
  };

  return {
    edit,
    isNew: !schedule,
    locked: !!schedule?.inUse,
    problems,
    canSave: problems.length === 0 && !saving,
    saving,
    error,
    handleNameChange: (name: string) => setEdit((current) => ({ ...current, name: name })),
    handleCodeChange: (code: string) => setEdit((current) => ({ ...current, code: code })),
    handleRowChange: (index: number, changes: Partial<ScheduleTimepointRow>) =>
      setEdit((current) => ({ ...current, rows: replaceAt(current.rows, index, (row) => ({ ...row, ...changes })) })),
    handleAddRow: () => setEdit((current) => ({ ...current, rows: [...current.rows, emptyTimepointRow()] })),
    handleRemoveRow: (index: number) => setEdit((current) => ({ ...current, rows: removeAt(current.rows, index) })),
    handleSave,
  };
};
